import React from "react";
import { motion } from "framer-motion";
import { 
  Building2, 
  Award, 
  Globe, 
  Pyramid, 
  TrendingUp, 
  ShieldCheck 
} from "lucide-react";
import { 
  fadeInUp, 
  staggerContainer, 
  scaleIn 
} from '../utils/animations';
import { useAdvancedScrollAnimation } from '../hooks/useAdvancedScrollAnimation';

const PolicyResearch = () => {
  const { sectionRef, triggerRef, isMobile } = useAdvancedScrollAnimation({
    fadeOutOnLeave: true,
    mobileBreakpoint: 768,
    fadeThreshold: 0.3
  });

  return (
    <div ref={triggerRef} className="w-full">
      <motion.section 
        ref={sectionRef}
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className={`
          py-16 md:py-20 
          bg-white 
          relative 
          overflow-hidden 
          ${isMobile ? 'px-4' : ''}
        `}
      >
        <div className="container mx-auto px-4 relative z-10">
          <motion.h2 
            className={`
              font-bold text-center mb-12 md:mb-16 text-gray-900
              ${isMobile 
                ? 'text-2xl sm:text-3xl' 
                : 'text-3xl md:text-4xl'
              }
            `}
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              Centers under Institute for Policy Research
            </motion.span>
          </motion.h2>

          <motion.div
            className={`
              grid 
              ${isMobile 
                ? 'grid-cols-1 gap-6' 
                : 'grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8'
              }
            `}
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <PolicyCard
              icon={<Building2 />}
              title="Centre for Statistics and Analysis"
              description="Transforming complex data into actionable insights through advanced statistical methodologies and comprehensive research approaches."
              delay={0.1}
            />
            <PolicyCard
              icon={<Award />}
              title="Centre for Sustainable Development"
              description="Pioneering innovative strategies for environmental sustainability, social equity, and holistic development across global contexts."
              delay={0.2}
            />
            <PolicyCard
              icon={<Globe />}
              title="Global Policy Dynamics"
              description="Analyzing intricate global challenges, international relations, and emerging geopolitical trends to provide strategic policy recommendations."
              delay={0.3}
            />
            <PolicyCard
              icon={<Pyramid />}
              title="Governance and Public Policy"
              description="Developing comprehensive frameworks for effective governance, institutional reform, and public policy innovation."
              delay={0.4}
            />
            <PolicyCard
              icon={<TrendingUp />}
              title="Economic Policy Research"
              description="Conducting in-depth economic analysis, forecasting emerging trends, and developing innovative solutions for sustainable economic growth."
              delay={0.5}
            />
            <PolicyCard
              icon={<ShieldCheck />}
              title="Social Impact and Resilience"
              description="Investigating societal challenges, designing intervention strategies, and promoting community resilience through evidence-based research."
              delay={0.6}
            />
          </motion.div>

          <motion.div 
            className="text-center mt-12 md:mt-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.7, duration: 0.6 }}
          >
            <motion.a 
              href="/research" 
              className="inline-block bg-amber-500 text-white px-8 py-3 rounded-lg shadow-lg hover:bg-amber-600 transition-all duration-300"
              whileHover={{ 
                scale: 1.05,
                boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2)"
              }}
              whileTap={{ scale: 0.95 }}
            >
              Explore Policy Research
            </motion.a>
          </motion.div>
        </div>

        {/* Enhanced Background Decorative Elements */}
        <motion.div
          className="absolute top-0 left-0 w-96 h-96 bg-amber-100/30 rounded-full -translate-x-1/2 -translate-y-1/2"
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, -90, 0],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        <motion.div
          className="absolute bottom-0 right-0 w-64 h-64 bg-amber-100/20 rounded-full translate-x-1/2 translate-y-1/2"
          animate={{
            scale: [1, 1.1, 1],
            rotate: [0, 90, 0],
            opacity: [0.2, 0.4, 0.2]
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        <motion.div
          className="absolute top-1/2 right-0 w-48 h-48 bg-amber-100/15 rounded-full translate-x-1/4"
          animate={{
            scale: [1, 1.15, 1],
            rotate: [0, 180, 0],
            opacity: [0.15, 0.25, 0.15]
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            ease: "linear"
          }}
        />
      </motion.section>
    </div>
  );
};

// Enhanced Policy Card Component
const PolicyCard = ({ icon, title, description, delay = 0 }) => {
  return (
    <motion.div
      className={`
        bg-white 
        p-4 md:p-6 lg:p-8 
        rounded-xl 
        shadow-lg 
        border 
        border-gray-100
        hover:shadow-xl 
        transition-all 
        duration-300
      `}
      variants={scaleIn}
      whileHover={{
        scale: 1.05,
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
        y: -10
      }}
      whileTap={{ scale: 0.98 }}
      transition={{ 
        type: "spring", 
        stiffness: 300,
        delay: delay 
      }}
    >
      <div className="flex items-start space-x-4 md:space-x-6">
        <motion.div
          className="text-amber-500 flex-shrink-0"
          whileHover={{ 
            rotate: 360,
            scale: 1.2
          }}
          transition={{ 
            duration: 0.6,
            type: "spring",
            stiffness: 200
          }}
        >
          {React.cloneElement(icon, { size: 36 })}
        </motion.div>
        <div>
          <motion.h4 
            className="text-base md:text-lg font-semibold mb-2 text-gray-800"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: delay + 0.2 }}
          >
            {title}
          </motion.h4>
          <motion.p 
            className="text-gray-600 text-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: delay + 0.4 }}
          >
            {description}
          </motion.p>
        </div>
      </div>
    </motion.div>
  );
};

export default PolicyResearch;